import type {
  OpportunityDetail,
  OpportunityListQuery,
  OpportunityListResponse,
  UpdateOpportunityRequest,
} from 'backend/types'
import { apiFetch } from '..'

/**
 * Opportunity型 (OpportunityListResponseの要素型)
 */
export type Opportunity = OpportunityListResponse['opportunities'][number]

/**
 * オポチュニティ作成データ型
 */
export interface CreateOpportunityData {
  salesforceProjectId: number
  opportunityTypeId: number
  title: string
  informationSource?: string | null
  description?: string | null
  isExpansion?: boolean
  startOn?: string | null
  contactName?: string | null
  amount?: number | null
  chatMembers?: { memberEmail: string }[]
  chatCustomerName?: string
}

/**
 * オポチュニティ一覧取得API
 *
 * タブや権限に応じたオポチュニティ一覧を取得します。
 *
 * @param params - クエリパラメータ
 * @param params.all - 全案件取得（特権ユーザー）
 * @param params.salesforceProjectMemberTypeId - ロール別フィルタ（1=Captain, 2=SMG, 4=PM, 5=Sales）
 * @param params.offset - オフセット（デフォルト: 0）
 * @param params.limit - 上限（オプション）
 * @returns オポチュニティ一覧レスポンス
 * @throws ApiError - 認証失敗（403）、権限不足（403）、サーバーエラー（500）
 */
export async function getOpportunities(params?: Partial<OpportunityListQuery>): Promise<OpportunityListResponse> {
  // クエリパラメータを構築
  const queryParams = new URLSearchParams()
  if (params?.all !== undefined) {
    queryParams.append('all', params.all ? 'true' : 'false')
  }
  if (params?.salesforceProjectMemberTypeId !== undefined) {
    queryParams.append('salesforceProjectMemberTypeId', params.salesforceProjectMemberTypeId.toString())
  }
  if (params?.offset !== undefined) {
    queryParams.append('offset', params.offset.toString())
  }
  if (params?.limit !== undefined) {
    queryParams.append('limit', params.limit.toString())
  }

  const queryString = queryParams.toString()
  const url = `/api/opportunities${queryString ? `?${queryString}` : ''}`

  return apiFetch(url, { method: 'GET' })
}

/**
 * オポチュニティ詳細取得API
 *
 * @param opportunityId - オポチュニティID
 * @returns オポチュニティ詳細レスポンス
 * @throws ApiError - 認証失敗（403）、見つからない（404）、サーバーエラー（500）
 */
export async function getOpportunity(opportunityId: number): Promise<OpportunityDetail> {
  return apiFetch<OpportunityDetail>(`/api/opportunity/${opportunityId}`, { method: 'GET' })
}

/**
 * オポチュニティ作成API
 *
 * @param data - 作成するオポチュニティデータ
 * @returns 作成されたオポチュニティ
 * @throws ApiError - 認証失敗（403）、バリデーションエラー（400）、サーバーエラー（500）
 */
export async function createOpportunity(
  data: CreateOpportunityData,
): Promise<Opportunity & { isCreatedChat: boolean }> {
  return apiFetch('/api/opportunity', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

/**
 * オポチュニティ更新API
 *
 * @param opportunityId - オポチュニティID
 * @param data - 更新データ
 * @returns 更新後のオポチュニティ詳細
 * @throws ApiError - 認証失敗（403）、見つからない（404）、サーバーエラー（500）
 */
export async function updateOpportunity(
  opportunityId: number,
  data: UpdateOpportunityRequest,
): Promise<OpportunityDetail> {
  return apiFetch<OpportunityDetail>(`/api/opportunity/${opportunityId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}
